import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useUserSettingsContext } from "@/contexts/Staffing/UserSettingsContext";
import { useDebouncedSave } from "@/hooks/useDebouncedSave";
import { useRestoreUserSettings } from "@/hooks/useUserSettings";
import type { UserSettings } from "@/utils/models";
import type { PersistedMonthlyExportSettings, PersistedWeeklyExportSettings } from "./types";

/**
 * How long a change sits before it is written.
 *
 * A column drag emits a change per pointer move, and a settings PUT carries the whole staffing row —
 * so writing per tick would be both wasteful and a race with itself.
 */
const SAVE_DEBOUNCE_MS = 600;

/** One debounce timer per document, so the two tabs cannot cancel each other's pending write. */
const MONTHLY_KEY = "staffingMonthlyExport";
const WEEKLY_KEY = "staffingWeeklyExport";

/**
 * The monthly document's own column, for the field-scoped restore endpoint.
 *
 * Scoped to this one column so "reset to default" cannot wipe the user's Daily Printout or weekly-view
 * settings, which share the same row.
 */
const MONTHLY_RESTORE_FIELDS = ["staffing_monthly_export_settings"] satisfies (keyof UserSettings)[];

export type ScheduleExportSettings = {
	/** The stored monthly document, or the shipped default when the settings query never runs. */
	monthly: PersistedMonthlyExportSettings;
	weekly: PersistedWeeklyExportSettings;
	/** Debounced: a column drag emits many changes and must not PUT per tick. */
	saveMonthly: (next: PersistedMonthlyExportSettings) => void;
	saveWeekly: (next: PersistedWeeklyExportSettings) => void;
	/** Whether to offer the department-default command at all. Support users only — see below. */
	canSaveAsDepartmentDefault: boolean;
	/**
	 * Push the given monthly document onto the department default AND every user row.
	 *
	 * Rejects rather than snackbaring, unlike the debounced `saveMonthly`: this one is an explicit,
	 * confirmed action, so the caller reports its outcome where it can also attribute the analytics.
	 */
	saveMonthlyAsDepartmentDefault: (next: PersistedMonthlyExportSettings) => Promise<void>;
	/** Drop the user's own monthly document and fall back to the department default. Rejects on failure. */
	resetMonthlyToDepartmentDefault: () => Promise<void>;
};

type SettingsPayload = { staffingMonthlyExport: PersistedMonthlyExportSettings } | { staffingWeeklyExport: PersistedWeeklyExportSettings };

/**
 * Load and persist both staffing-export tabs' settings, per user and department.
 *
 * Two independent JSONB documents on `user_settings`, sent and received verbatim — see
 * `PersistedMonthlyExportSettings`. The provider already falls back to the shipped defaults for a user
 * whose settings query never runs (a non-head-nurse), so there is no fallback to repeat here.
 *
 * Built on `useDebouncedSave` rather than a local timer specifically because it FLUSHES on unmount
 * instead of cancelling. Both tabs unmount for real — `ScheduleExportBody` renders one or the other, so
 * switching tabs unmounts the one being left, and closing the popup unmounts both. Cancelling meant the
 * last change before either action was shown in the UI and never written.
 */
export const useScheduleExportSettings = (): ScheduleExportSettings => {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const { settings, updateSettings } = useUserSettingsContext();
	const { mutateAsync: restoreUserSettings } = useRestoreUserSettings();
	const { userInfo } = useAuth();

	// `saveFn` is read through a ref inside the hook, so this closing over a fresh `updateSettings`
	// every render does not restart a pending timer.
	//
	// The rejection is surfaced, not swallowed: the write is debounced and fire-and-forget, so without
	// this a failed PUT left the panel showing settings that were never stored and the user found out
	// on their next visit.
	const schedule = useDebouncedSave<SettingsPayload>(
		(payload) =>
			void updateSettings(payload).catch((error: unknown) =>
				// The second argument is the tracking payload, so the failure is reported as well as shown.
				showErrorSnackbar(t("download.settings.save.failed"), { document: Object.keys(payload)[0] }, error),
			),
		SAVE_DEBOUNCE_MS,
	);

	const saveMonthly = useCallback((next: PersistedMonthlyExportSettings) => schedule(MONTHLY_KEY, { staffingMonthlyExport: next }), [schedule]);
	const saveWeekly = useCallback((next: PersistedWeeklyExportSettings) => schedule(WEEKLY_KEY, { staffingWeeklyExport: next }), [schedule]);

	/**
	 * Support-only by product decision — deliberately NOT `useSaveAsDefaultEnabled`, which also honours
	 * the `save_as_default_enabled` flag. Mirrors the Profile weekly export
	 * (`useWeekStaffScheduleExportSettings`), which made the same call for the same reason: a department
	 * default reaches every user's row, so it is an onboarding action rather than a scheduler's.
	 *
	 * A UI gate only. The backend independently requires support-or-flag plus the head-nurse role
	 * (`backend/routes/user_settings.py`), so this narrows what is offered, never what is enforced.
	 */
	const canSaveAsDepartmentDefault = userInfo.support;

	const saveMonthlyAsDepartmentDefault = useCallback(
		async (next: PersistedMonthlyExportSettings): Promise<void> => {
			if (!canSaveAsDepartmentDefault) return;
			// Only this key is populated, so the PUT carries one column and `save_as_department_default`
			// propagates just that column onto every user row — nobody's Daily Printout setup is touched.
			await updateSettings({ staffingMonthlyExport: next }, true);
		},
		[canSaveAsDepartmentDefault, updateSettings],
	);

	/**
	 * Back to the department default.
	 *
	 * `useRestoreUserSettings` writes its response into the settings query cache, so `monthly` above
	 * becomes the department default and the tab's `useHydratedSettings` re-adopts it on the new
	 * identity — no local reset needed here.
	 *
	 * Re-scheduling the restored document afterwards supersedes any in-flight debounced save under the
	 * same key, which would otherwise land after the restore and immediately undo it. `useDebouncedSave`
	 * exposes no cancel, so replacing the pending payload is the way to do that.
	 */
	const resetMonthlyToDepartmentDefault = useCallback(async (): Promise<void> => {
		const restored = await restoreUserSettings(MONTHLY_RESTORE_FIELDS);
		saveMonthly(restored.staffing_monthly_export_settings);
	}, [restoreUserSettings, saveMonthly]);

	return {
		monthly: settings.staffingMonthlyExport,
		weekly: settings.staffingWeeklyExport,
		saveMonthly,
		saveWeekly,
		canSaveAsDepartmentDefault,
		saveMonthlyAsDepartmentDefault,
		resetMonthlyToDepartmentDefault,
	};
};
